import { IAdditionalInfo } from '@/models';

export function reconcileStarEditDuplicates(
  items: IAdditionalInfo[],
): IAdditionalInfo[] {
  if (!items || items.length <= 1) return items;

  const result = items.map((item) => ({ ...item }));
  const indicesToRemove = new Set<number>();

  // Group non-deleted items by (createdBy._id, normalised description).
  const groups = new Map<string, number[]>();

  for (let i = 0; i < result.length; i++) {
    if (result[i].isDeleted) continue;

    const item = result[i];
    const createdById =
      typeof item.createdBy === 'string' ? item.createdBy : item.createdBy?._id;
    const normalizedDesc = item.description.replace(/^\*\s*/, '').trim();

    // Skip items with no meaningful text (e.g. a lone "*").
    if (!normalizedDesc) continue;

    const key = `${createdById}::${normalizedDesc}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(i);
  }

  for (const [, indices] of groups) {
    if (indices.length <= 1) continue;

    const hasStarred = indices.some((i) =>
      result[i].description.startsWith('*'),
    );
    const hasNonStarred = indices.some(
      (i) => !result[i].description.startsWith('*'),
    );

    // Only deduplicate when the group has *mixed* star/non-star
    // descriptions – that is the signature of a star/un-star edit.
    if (!hasStarred || !hasNonStarred) continue;

    // Find the earliest createdAt (original send time) and the latest
    // entry (the user's current intended version).
    let earliestCreatedAt = result[indices[0]].createdAt;
    let latestIndex = indices[0];
    let latestTime = new Date(result[indices[0]].createdAt).getTime();

    for (const i of indices) {
      const t = new Date(result[i].createdAt).getTime();
      if (t < new Date(earliestCreatedAt).getTime()) {
        earliestCreatedAt = result[i].createdAt;
      }
      if (t > latestTime) {
        latestTime = t;
        latestIndex = i;
      }
    }

    // Mark every entry except the latest for removal.
    for (const i of indices) {
      if (i !== latestIndex) {
        indicesToRemove.add(i);
      }
    }

    // Patch the surviving entry with the original timestamp and the
    // edited flag so the UI renders correctly.
    result[latestIndex] = {
      ...result[latestIndex],
      createdAt: earliestCreatedAt,
      isEdited: true,
    };
  }

  return result.filter((_, index) => !indicesToRemove.has(index));
}
